import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './RuleForm.css';
import './ConditionalRuleForm.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function UnconditionalRuleForm({ fields, rules, docType, docVersion, onRuleUpdate, showToast, onFieldSelectionModeChange }) {
  const [step, setStep] = useState(0); // 0: initial, 1: select group fields, 2: details form
  const [formData, setFormData] = useState({
    group: [],
    name: '',
    severity: 'required',
    requirement: 'exactlyOne'
  });
  const [selectedGroupFields, setSelectedGroupFields] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null); // Track which rule is being edited
  
  // Refs for action buttons to enable smooth scrolling
  const step1ActionsRef = useRef(null);
  const step2ActionsRef = useRef(null);
  
  // Scroll to action buttons when step changes
  useEffect(() => {
    const scrollToActions = () => {
      let actionsRef = null;
      if (step === 1) {
        actionsRef = step1ActionsRef.current;
      } else if (step === 2) {
        actionsRef = step2ActionsRef.current;
      }
      
      if (actionsRef) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          actionsRef.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }, 100);
      }
    };
    
    scrollToActions();
  }, [step]);
  
  // Also scroll when group fields list changes (grows)
  useEffect(() => {
    if (step === 1 && step1ActionsRef.current) {
      setTimeout(() => {
        step1ActionsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [selectedGroupFields.length, step]);

  const handleStartNewRule = () => {
    setEditingIndex(null);
    setStep(1);
    setFormData({
      group: [],
      name: '',
      severity: 'required',
      requirement: 'exactlyOne'
    });
    setSelectedGroupFields([]);
    // Notify parent that we're in group field selection mode
    if (onFieldSelectionModeChange) {
      onFieldSelectionModeChange({ mode: 'group_fields', step: 1 });
    }
  };

  const handleGroupFieldToggle = React.useCallback((fieldId) => {
    setSelectedGroupFields(prev => {
      if (prev.includes(fieldId)) {
        // Remove field
        return prev.filter(id => id !== fieldId);
      } else {
        // Add field
        return [...prev, fieldId];
      }
    });
  }, []);

  // Handle field selection from PDF
  const handleFieldSelectFromPDF = React.useCallback((field) => {
    if (step === 1) {
      // Step 1: Toggle group field
      handleGroupFieldToggle(field.id);
    }
  }, [step, handleGroupFieldToggle]);

  // Expose handler to parent via useEffect
  React.useEffect(() => {
    if (onFieldSelectionModeChange && step === 1) {
      onFieldSelectionModeChange({ 
        mode: 'group_fields', 
        step: 1,
        selectedFields: selectedGroupFields,
        onFieldSelect: handleFieldSelectFromPDF
      });
    } else if (onFieldSelectionModeChange && step === 0) {
      onFieldSelectionModeChange(null);
    }
  }, [step, selectedGroupFields, handleFieldSelectFromPDF, onFieldSelectionModeChange]);

  const handleGroupFieldsDone = () => {
    if (selectedGroupFields.length === 0) {
      showToast('Please select at least one field for the group', 'warning');
      return;
    }
    setFormData({ ...formData, group: selectedGroupFields });
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIndex !== null) {
        // Update existing rule
        await axios.put(`${API_BASE}/rules/${docType}/unconditional/${editingIndex}`, formData);
        showToast('Unconditional rule updated successfully!', 'success');
      } else {
        // Create new rule
        await axios.post(`${API_BASE}/rules/${docType}/unconditional`, formData);
        showToast('Unconditional rule added successfully!', 'success');
      }
      
      onRuleUpdate();
      setStep(0);
      setEditingIndex(null);
      setFormData({
        group: [],
        name: '',
        severity: 'required',
        requirement: 'exactlyOne'
      });
      setSelectedGroupFields([]);
      // Notify parent that selection mode is off
      if (onFieldSelectionModeChange) {
        onFieldSelectionModeChange(null);
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      showToast('Error saving rule', 'error');
    }
  };

  const handleCancel = () => {
    setStep(0);
    setEditingIndex(null);
    setFormData({
      group: [],
      name: '',
      severity: 'required',
      requirement: 'exactlyOne'
    });
    setSelectedGroupFields([]);
    // Notify parent that selection mode is off
    if (onFieldSelectionModeChange) {
      onFieldSelectionModeChange(null);
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const params = new URLSearchParams();
      if (docVersion) params.append('version', docVersion);
      await axios.delete(`${API_BASE}/rules/${docType}/unconditional/${index}?${params.toString()}`);
      onRuleUpdate();
      showToast('Rule deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting rule:', error);
      showToast('Error deleting rule', 'error');
    }
  };

  const handleEditRule = (index) => {
    const rule = rules[index];
    if (!rule) return;

    setEditingIndex(index);
    
    // Populate form data
    setFormData({
      group: rule.group || [],
      name: rule.name || '',
      severity: rule.severity || 'required',
      requirement: rule.requirement || 'exactlyOne'
    });

    // Populate group fields
    setSelectedGroupFields(rule.group || []);

    // Start at step 1 (group field selection)
    setStep(1);
    // Notify parent that we're in group field selection mode
    if (onFieldSelectionModeChange) {
      onFieldSelectionModeChange({ mode: 'group_fields', step: 1 });
    }
  };

  return (
    <div className="rule-form">
      <div className="rules-list">
        {rules.map((rule, index) => (
          <div key={index} className="rule-item">
            <div className="rule-item-header">
              <h4>{rule.name || 'Unnamed Rule'}</h4>
              <div className="rule-item-actions">
                <button
                  className="btn-secondary-small"
                  onClick={() => handleEditRule(index)}
                  style={{ marginRight: '8px' }}
                >
                  Edit
                </button>
                <button
                  className="btn-danger-small"
                  onClick={() => handleDelete(index)}
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="rule-item-details">
              <p><strong>Group Fields:</strong> {rule.group.join(', ')}</p>
              <p><strong>Requirement:</strong> {rule.requirement}</p>
              <p><strong>Severity:</strong> {rule.severity}</p>
            </div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <button className="btn-add" onClick={handleStartNewRule}>
          + Add New Unconditional Rule
        </button>
      )}

      {step === 1 && (
        <div className="conditional-wizard-step">
          <h3>Step 1: Select Group Fields</h3>
          <p className="step-description">Click on fields in the PDF document to select them as group fields. Click again to deselect.</p>
          
          {selectedGroupFields.length > 0 && (
            <div className="selected-dependent-fields-list">
              <h4>Selected Group Fields:</h4>
              {selectedGroupFields.map((fieldId) => {
                const field = fields.find(f => f.id === fieldId);
                return (
                  <div key={fieldId} className="selected-dependent-field-item">
                    <div className="field-info">
                      <strong title={fieldId}>{field?.name || fieldId}</strong>
                    </div>
                    <div className="field-config-inputs">
                      <button
                        type="button"
                        className="btn-danger-small"
                        onClick={() => handleGroupFieldToggle(fieldId)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedGroupFields.length === 0 && (
            <div className="no-selection-message">
              No fields selected yet. Click on fields in the PDF document to add them.
            </div>
          )}

          <div className="form-actions" ref={step1ActionsRef}>
            <button type="button" className="btn-primary" onClick={handleGroupFieldsDone} disabled={selectedGroupFields.length === 0}>
              Done Selecting Group Fields
            </button>
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} className="conditional-form">
          <h3>{editingIndex !== null ? 'Edit Rule Details' : 'Step 2: Add Rule Details'}</h3>
          
          <div className="form-group">
            <label>Rule Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Requirement</label>
            <select
              value={formData.requirement}
              onChange={(e) => setFormData({ ...formData, requirement: e.target.value })}
            >
              <option value="exactlyOne">Exactly One</option>
              <option value="atleastOne">At Least One</option>
              <option value="all">All</option>
            </select>
          </div>

          <div className="form-group">
            <label>Severity</label>
            <select
              value={formData.severity}
              onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
            >
              <option value="required">Required</option>
              <option value="optional">Optional</option>
            </select>
          </div>

          <div className="form-group">
            <label>Selected Group Fields</label>
            <div className="dependent-fields-list">
              {selectedGroupFields.map((fieldId) => {
                const field = fields.find(f => f.id === fieldId);
                return (
                  <div key={fieldId} className="dependent-field-item">
                    <span>{field?.name || fieldId}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="form-actions" ref={step2ActionsRef}>
            <button type="submit" className="btn-primary">
              {editingIndex !== null ? 'Update Unconditional Rule' : 'Add Unconditional Rule'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setStep(1)}>
              Back
            </button>
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default UnconditionalRuleForm;
